import type { IChannel } from "containers/Channels/types";
import type { ChannelParams } from "containers/TermFormContainer";
import { channelHasSelectableBaseColumns } from "containers/TermFormContainer/ChannelsStep/ChannelBaseColumnsFields";
import {
    getCalculationBaseColumnOptions,
    resolveCalculationBaseColumn,
} from "containers/TermFormContainer/ChannelsStep/calculationBaseColumnUtils";
import { resolveChannelMilestoneBaseColumn } from "containers/TermFormContainer/ChannelsStep/milestoneBaseColumnUtils";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getCalculationBaseColumnLabels } from "store/calculationBaseColumnLabelsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import styles from "./style.module.scss";

type ChannelBaseColumnsRecapProps = {
    channel: ChannelParams | undefined;
    fullChannelData: IChannel | undefined;
    /** When true, also show the milestone base (group/channel has tiers). */
    hasMilestones?: boolean;
};

/**
 * Read-only reminder of the contract calculation / milestone bases chosen for a channel.
 */
const ChannelBaseColumnsRecap = ({ channel, fullChannelData, hasMilestones = false }: ChannelBaseColumnsRecapProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { labels, labelsStatus } = useAppSelector((state) => state.calculationBaseColumnLabels);

    useEffect(() => {
        if (labelsStatus === "idle") {
            dispatch(getCalculationBaseColumnLabels());
        }
    }, [labelsStatus, dispatch]);

    const labelByColumn = useMemo(() => {
        return Object.fromEntries(
            getCalculationBaseColumnOptions(labels, t).map((option) => [option.value, option.label]),
        );
    }, [labels, t]);

    if (!fullChannelData || !channelHasSelectableBaseColumns(fullChannelData)) return null;

    const showMilestoneBase = hasMilestones || !!fullChannelData.hasMilestones;
    const baseColumn = resolveCalculationBaseColumn(fullChannelData, channel?.calculationBaseColumn);
    const milestoneColumn = resolveChannelMilestoneBaseColumn(fullChannelData, channel);

    return (
        <div className={styles["base-columns-recap"]}>
            <div className={styles.chip}>
                <span className={styles.label}>{t("ConnectTitle.CalculationBaseColumn")}</span>
                <span className={styles.value}>{labelByColumn[baseColumn] ?? baseColumn}</span>
            </div>
            {showMilestoneBase && (
                <div className={styles.chip}>
                    <span className={styles.label}>{t("ConnectTitle.MilestoneBaseColumn")}</span>
                    <span className={styles.value}>{labelByColumn[milestoneColumn] ?? milestoneColumn}</span>
                </div>
            )}
        </div>
    );
};

export default ChannelBaseColumnsRecap;
